import { StarjackTable } from '@starjack/shared-components';
import { FunctionComponent, useMemo, useState } from 'react';
import { Ticket } from '../model/ticket.types';
import { LiftOperator } from '../../liftoperator/model/liftoperator.types';

import {
    useTicketColumns,
    TicketTableColumnId,
} from '../lib/hooks/useTicketColumns';
import { Box, Drawer, SvgIcon, Typography } from '@mui/material';
import {
    useIdentificationBlocksFactory,
    useTicketDetailsBlocksFactory,
    IdentificationContentBlockId,
    TicketContentBlockId,
} from '../lib/hooks/useTicketDetails';
import { TicketDrawerBlock } from '../ui/ticketDrawerContent/ticketDrawerBlock';
import { FlexContainer } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@heroicons/react/24/solid/XMarkIcon';

export type TicketsOverviewContainerProps = {
    currentPageTickets: Ticket[];
    ticketsTotal: number;
    ticketsPerPage: number;
    ticketsLoading: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    hideColumns?: TicketTableColumnId[];
    hideDetailFields?: TicketContentBlockId[];
    hideIdentificationFields?: IdentificationContentBlockId[];
    onRowClick?: (ticket: Ticket) => void;
    liftOperators?: LiftOperator[];
    defaultSortColumn?: string;
};

export const TicketsOverviewContainer: FunctionComponent<
    TicketsOverviewContainerProps
> = ({
    currentPageTickets = [],
    ticketsTotal,
    ticketsPerPage,
    ticketsLoading,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    defaultSortColumn,
    hideColumns = [],
    liftOperators = [],
    hideDetailFields = [],
    hideIdentificationFields = [],
}) => {
    const [drawerTicketId, setDrawerTicketId] = useState<string>(null);

    const columns = useTicketColumns({ liftOperators });

    const columnsFilteredByConfig = useMemo(() => {
        return columns.filter(
            (column) =>
                !hideColumns.find(
                    (hideColumn) => hideColumn === column.columnId
                )
        );
    }, [columns, hideColumns]);

    const drawerTicket =
        currentPageTickets.find((cT) => cT.id === drawerTicketId) || null;

    // Get the factory functions from hooks
    const createIdentificationBlocks = useIdentificationBlocksFactory();
    const createTicketDetailsBlocks = useTicketDetailsBlocksFactory();

    const ticketDrawerBlocks = useMemo(() => {
        if (!drawerTicketId || !drawerTicket) return [];

        const identificationBlocks = drawerTicket?.identifications.map(
            (identification) =>
                createIdentificationBlocks(identification).filter(
                    (iB) => !hideIdentificationFields.includes(iB.blockId)
                )
        );

        const liftOperator = liftOperators.find(
            (l) => l.id === drawerTicket?.liftOperatorId
        );

        return createTicketDetailsBlocks({
            ticket: drawerTicket,
            identifications: identificationBlocks,
            liftOperator,
        });
    }, [
        drawerTicketId,
        drawerTicket,
        hideDetailFields,
        hideIdentificationFields,
        liftOperators,
        createIdentificationBlocks,
        createTicketDetailsBlocks,
    ]);

    return (
        <>
            <StarjackTable<Ticket>
                itemId={'id'}
                items={currentPageTickets}
                columns={columnsFilteredByConfig}
                count={ticketsTotal}
                rowsPerPage={ticketsPerPage}
                loading={ticketsLoading}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                onRowClick={(item) => {
                    setDrawerTicketId(item.id);

                    onRowClick?.(item);
                }}
                rowsSelectable={false}
                defaultSortColumn={defaultSortColumn || 'orderDate'}
                selectRow={(ticket) => ticket.id === drawerTicket?.id}
            />
            <Drawer
                anchor='right'
                open={!!drawerTicketId}
                onClose={() => {
                    setDrawerTicketId(null);
                }}
                variant='persistent'
            >
                <Box
                    sx={{
                        width: '580px',
                    }}
                >
                    <FlexContainer
                        sx={(theme) => ({
                            height: '72px',
                            position: 'fixed',
                            width: '580px',
                            alignItems: 'center',
                            backgroundColor: theme.palette.primary.dark,
                            color: '#fff',
                            p: 4,
                        })}
                    >
                        <Typography variant='h6'>
                            {drawerTicket?.name}
                        </Typography>

                        <IconButton
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={() => {
                                setDrawerTicketId(null);
                            }}
                        >
                            <SvgIcon
                                sx={{
                                    color: '#fff',
                                    fontSize: '28px',
                                }}
                            >
                                <CloseIcon />
                            </SvgIcon>
                        </IconButton>
                    </FlexContainer>

                    <Box
                        sx={{
                            mt: '72px',
                        }}
                    >
                        {ticketDrawerBlocks.map((block) => (
                            <TicketDrawerBlock
                                key={block.blockId}
                                header={block.header}
                            >
                                {block.content}
                            </TicketDrawerBlock>
                        ))}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};
